import { ProductNames } from "@/common/data/Products"
import {
  CheckList,
  CheckListItemDesc,
  CheckListItemTitle,
  CheckListItemWrapper
} from "../CheckListStyle"
import styled, { useTheme } from "styled-components"
import CheckIcon from "@/common/assets/image/contactUs/check.svg"
import { theme } from "@/common/Mixin"
import Button from "../_UI/ButtonNew"
import { useTranslation } from "next-i18next"
import NextImg from "../NextImg"
import { MouseEventHandler } from "react"

interface ButtonProps {
  id?: string
  text: string
  href?: string
  type?: "primary" | "subtle"
  onClick?: MouseEventHandler<HTMLButtonElement>
}

interface Detail {
  title: string
  list: CheckList
}

export interface PricingCardProps {
  isRecommended?: boolean
  key?: any
  title?: string
  description?: string
  button?: ButtonProps
  productLinks: ProductNames[]
  detail: Detail[]
  detailsRef?: React.RefObject<HTMLDivElement>[]
  heights?: number[]
}

const productName2TextKey: Record<ProductNames, string> = {
  [ProductNames.AllProducts]: "Pricing.allProducts.name",
  [ProductNames.User360]: "Pricing.user360.name",
  [ProductNames.WalletBuilder]: "Pricing.walletBuilder.name",
  [ProductNames.OAuthSDK]: "Pricing.oauthSDK.name",
  [ProductNames.WalletAPI]: "Pricing.walletAPI.name",
  [ProductNames.WalletSDK]: "Pricing.walletSDK.name",
  [ProductNames.WalletExtension]: "Pricing.walletExtension.name",
  [ProductNames.WalletApplication]: "Pricing.walletApp.name",
  [ProductNames.AssetPro]: "Pricing.assetPro.name",
  [ProductNames.NFTAirdrop]: "Pricing.nftAirdrop.name",
  [ProductNames.NFTBoost]: "Pricing.nftBoost.name",
  [ProductNames.ComplianceLite]: "Pricing.complianceLite.name",
  [ProductNames.CompliancePro]: "Pricing.compliancePro.name",
  [ProductNames.ComplianceAPI]: "Pricing.complianceAPI.name",
  [ProductNames.ComplyFlow]: "Pricing.complyFlow.name",
  [ProductNames.Transfer]: "Pricing.transfer.name"
}

const productName2Route: Record<ProductNames, string> = {
  [ProductNames.AllProducts]: "/products",
  [ProductNames.User360]: "/products/user360",
  [ProductNames.WalletBuilder]: "/products/wallet-builder",
  [ProductNames.OAuthSDK]: "/products/oauth-sdk",
  [ProductNames.WalletAPI]: "/products/wallet-api",
  [ProductNames.WalletSDK]: "/products/wallet-sdk",
  [ProductNames.WalletExtension]: "/products/wallet-extension",
  [ProductNames.WalletApplication]: "/products/wallet",
  [ProductNames.AssetPro]: "/products/asset-pro",
  [ProductNames.NFTAirdrop]: "/products/nft-airdrop",
  [ProductNames.NFTBoost]: "/products/nft-boost",
  [ProductNames.ComplianceLite]: "/products/compliance-lite",
  [ProductNames.CompliancePro]: "/products/compliance-pro",
  [ProductNames.ComplianceAPI]: "/products/complinace-api",
  [ProductNames.ComplyFlow]: "/products/comply-flow",
  [ProductNames.Transfer]: "/products/transfer"
}

export default function PricingCard({
  isRecommended = false,
  title,
  description,
  button,
  productLinks,
  detail,
  detailsRef,
  heights
}: PricingCardProps) {
  const theme = useTheme()
  const { t } = useTranslation(["common"])
  return (
    <LayoutWrapper isRecommended={isRecommended}>
      {isRecommended && (
        <RecommendedTag className="button-l">
          {t("Pricing.recommended")}
        </RecommendedTag>
      )}
      <TopWrapper>
        {title && <TitleWrapper>{title}</TitleWrapper>}
        {description && (
          <DescWrapper className="title">{description}</DescWrapper>
        )}
        {button && (
          <div style={{ display: "flex", width: "100%" }}>
            <Button
              id={button.id}
              styleType={button.type ?? "primary"}
              content={button.text}
              href={button.href ?? ""}
              onClick={button.onClick}
              arrowType="none"
              wide
            />
          </div>
        )}
      </TopWrapper>
      <BottomWrapper>
        {detail.map((detailItem, index) => (
          <DetailWrapper
            ref={detailsRef && detailsRef[index]}
            height={heights && `${heights[index]}px`}
            key={detailItem.title}
          >
            <DetailTitleWrapper className="body">
              {detailItem.title}
            </DetailTitleWrapper>
            <DetailListWrapper>
              {detailItem.list.list.map((item, index) => (
                <CheckListItemWrapper
                  style={{ paddingLeft: 0 }}
                  key={`${item.title}_${index}`}
                >
                  {item.noIcon ? null : (
                    <NextImg
                      style={{ width: "24px" }}
                      src={CheckIcon.src}
                      alt="Check Icon"
                    />
                  )}
                  <p>
                    {item.title && (
                      <CheckListItemTitle className="body">
                        {item.title}
                      </CheckListItemTitle>
                    )}
                    {item.description && (
                      <CheckListItemDesc
                        color={detailItem.list.listColor}
                        className="body"
                      >
                        {item.description}
                      </CheckListItemDesc>
                    )}
                  </p>
                </CheckListItemWrapper>
              ))}
            </DetailListWrapper>
          </DetailWrapper>
        ))}
      </BottomWrapper>
      <ProductLinksWrapper>
        {productLinks.map((product, index) => (
          <Button
            style={{
              borderRadius: "16px",
              border: `1px solid ${theme.colors.textFamily.secondary}`
            }}
            key={`${product}_${index}`}
            styleType="text"
            className="button-m-line lightGray"
            content={t(productName2TextKey[product])}
            href={productName2Route[product]}
            arrowType="none"
          />
        ))}
      </ProductLinksWrapper>
    </LayoutWrapper>
  )
}

const RecommendedTag = styled.span`
  display: flex;
  padding: 12px 32px;
  justify-content: center;
  align-items: flex-start;
  position: absolute;
  left: 50%;
  transform: translate(-50%, -50%);

  background-color: ${theme.color.white};
  color: ${theme.color.textGray};
  z-index: 1;

  &:after {
    content: "";
    width: 0;
    height: 0;
    position: absolute;
    border-style: solid;
    border-width: 8px 6px 0 6px;
    border-color: ${theme.color.white} transparent transparent transparent;
    bottom: -8px;
  }
`

const LayoutWrapper = styled.div<{ isRecommended: boolean }>`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  ${props =>
    props.isRecommended
      ? `
  border-radius: 16px;
  border: 1px solid ${props.theme.colors.primary};
    `
      : `
      border-radius: 16px;
      border: 1px solid ${props.theme.colors.textFamily.disabled};
    `}
`

const TopWrapper = styled.div`
  display: flex;
  padding: 40px 32px 20px 32px;
  width: 100%;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  flex-shrink: 0;
  align-self: stretch;
  border-radius: 16px 16px 0px 0px;
  background: ${theme.color.dark};

  @media (${theme.lg}) {
    height: auto;
  }
`

const TitleWrapper = styled.h3`
  height: 50px;
  color: ${theme.color.textContrast};
  @media (${theme.lg}) {
    height: auto;
  }
`

const DescWrapper = styled.span`
  height: 65px;
  text-align: center;
  color: ${theme.color.textLightGray};
  @media (${theme.lg}) {
    height: auto;
  }
`

const ProductLinksWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  align-content: flex-start;
  gap: 10px;
  align-self: stretch;
  flex-wrap: wrap;
  flex-grow: 1;
  padding: 0 32px 16px 32px;
  @media (${theme.lg}) {
    height: auto;
  }
`

const BottomWrapper = styled.div`
  display: flex;
  padding: 32px;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;

  @media (${theme.lg}) {
    height: auto;
  }
`

const DetailWrapper = styled.div<{ height?: string }>`
  display: grid;
  grid-template-columns: 1fr 2.8fr;
  min-height: ${props => props.height ?? "auto"};
  gap: 20px;
  align-self: stretch;

  @media (${theme.ssm}) {
    display: flex;
    flex-direction: column;
  }

  @media (${theme.lg}) {
    grid-template-columns: 1fr 2fr;
    min-height: unset;
  }
`

const DetailTitleWrapper = styled.span`
  color: ${theme.color.textDisabled};
  white-space: pre-line;
`

const DetailListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  flex: 1 0 0;
`
